import React, {useEffect, useState} from 'react';
import {Breadcrumb, Button, Card, message, Modal, Skeleton, theme, Typography} from 'antd';
import {DeleteOutlined, HomeOutlined, PlusOutlined} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {projectsApi, type ProjectResponse} from '@/api/projects';
import {ProjectsTable} from '@components/table';
import {CreateProjectModal} from '@components/dialog';

const {Title, Text} = Typography;

const ProjectsList: React.FC = () => {
	const [projects, setProjects] = useState<ProjectResponse[]>([]);
	const [loading, setLoading] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [deleting, setDeleting] = useState(false);
	const {
		token: {borderRadiusLG},
	} = theme.useToken();

	const load = async () => {
		setLoading(true);
		const {data, error} = await projectsApi.list();
		if (error) {
			message.error(error.message || 'Не удалось загрузить проекты');
		} else if (data) {
			setProjects(data);
		}
		setLoading(false);
	};

	useEffect(() => {
		void load();
	}, []);

	const selectedOwnedProjects = projects.filter(p => selectedRowKeys.includes(p.id) && !p.shared);

	const handleDeleteSelected = () => {
		if (selectedOwnedProjects.length === 0) {
			return;
		}

		Modal.confirm({
			title: 'Удалить проекты',
			content: `Удалить выбранные проекты (${selectedOwnedProjects.length})? Это действие необратимо.`,
			okText: 'Удалить',
			okType: 'danger',
			cancelText: 'Отмена',
			onOk: async () => {
				setDeleting(true);
				try {
					for (const p of selectedOwnedProjects) {
						const {error} = await projectsApi.delete(p.id);
						if (error) {
							message.error(error.message || 'Не удалось удалить проект');
							return;
						}
					}
					message.success(
						selectedOwnedProjects.length === 1 ? 'Проект удалён' : 'Проекты удалены'
					);

					setSelectedRowKeys([]);
					await load();
				} finally {
					setDeleting(false);
				}
			},
		});
	};

	return (
		<>
			<Breadcrumb
				style={{marginBottom: 16}}
				items={[
					{
						title: (
							<Link to="/">
								<HomeOutlined />
							</Link>
						),
					},
					{title: 'Проекты'},
				]}
			/>

			<Title level={4} style={{margin: '0 0 24 0'}}>
				Проекты
			</Title>
			<Card style={{borderRadius: borderRadiusLG}}>
				<div
					style={{
						display: 'flex',
						justifyContent: 'end',
						alignItems: 'center',
						gap: 8,
						marginBottom: 24,
					}}
				>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => setModalOpen(true)}
					>
						Создать проект
					</Button>
					<Button
						type="primary"
						danger
						variant="solid"
						icon={<DeleteOutlined />}
						disabled={selectedOwnedProjects.length === 0}
						loading={deleting}
						aria-label="Удалить выбранные проекты"
						title="Удалить выбранные"
						onClick={handleDeleteSelected}
					/>
				</div>
				{loading ? (
					<Skeleton active paragraph={{rows: 4}} />
				) : projects.length === 0 ? (
					<Text type="secondary">Нет проектов. Создайте первый проект.</Text>
				) : (
					<ProjectsTable
						projects={projects}
						selectedRowKeys={selectedRowKeys}
						setSelectedRowKeys={setSelectedRowKeys}
					/>
				)}
			</Card>
			<CreateProjectModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
		</>
	);
};

export default ProjectsList;
